package com.evoapps.evoshift.ui.user

import androidx.compose.runtime.remember
import com.evoapps.evoshift.widgets.LocalOnColours
import kotlinx.datetime.LocalDate
import evoshift.composeapp.generated.resources.dashboard_today
import com.evoapps.evoshift.ui.util.dayAndDate
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.widgets.RegularCard
import com.evoapps.evoshift.widgets.StandoutCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.dashboard_currently_at_work
import evoshift.composeapp.generated.resources.dashboard_more_employees
import evoshift.composeapp.generated.resources.dashboard_next_shift
import evoshift.composeapp.generated.resources.dashboard_no_one_working
import evoshift.composeapp.generated.resources.dashboard_no_upcoming_shifts
import evoshift.composeapp.generated.resources.dashboard_staff_role
import evoshift.composeapp.generated.resources.dashboard_team_schedule
import evoshift.composeapp.generated.resources.dashboard_tomorrow
import evoshift.composeapp.generated.resources.dashboard_no_one_scheduled
import evoshift.composeapp.generated.resources.dashboard_was_working_today
import evoshift.composeapp.generated.resources.dashboard_working_next
import evoshift.composeapp.generated.resources.at_work_title
import evoshift.composeapp.generated.resources.weather_strip_title
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime
import com.evoapps.evoshift.domain.util.AppClock.now
import org.jetbrains.compose.resources.stringResource

@Composable
fun NextShiftCard(
    shift: Shift?,
    workplaceName: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    StandoutCard(
        onClick = onClick,
        // [modifier] after the padding so a hover lift outlines the card, not its margin.
        modifier = Modifier
            .fillMaxWidth()
            .height(180.dp)
            .padding(vertical = 8.dp)
            .then(modifier)
    ) {
        Column(
            modifier = Modifier
                .padding(24.dp)
                .fillMaxSize(),
            verticalArrangement = Arrangement.SpaceBetween,
            horizontalAlignment = Alignment.Start
        ) {
            Text(
                text = stringResource(Res.string.dashboard_next_shift),
                style = MaterialTheme.typography.labelMedium.copy(
                    letterSpacing = 1.sp,
                    fontWeight = FontWeight.Bold
                ),
                color = LocalOnColours.current.accent
            )

            if (shift != null) {
                Column {
                    val start = shift.plannedTime.start
                    val end = shift.plannedTime.end
                    val startTime = if (start.length > 16) start.substring(11, 16) else start
                    val endTime = if (end.length > 16) end.substring(11, 16) else end
                    
                    val date = LocalDate.parse(shift.date)
                    val today = remember { now().toLocalDateTime(TimeZone.currentSystemDefault()).date }
                    val dayLabel = when (date) {
                        today -> stringResource(Res.string.dashboard_today)
                        today.plus(DatePeriod(days = 1)) -> stringResource(Res.string.dashboard_tomorrow)
                        else -> dayAndDate(date)
                    }
                    Text(
                        text = "$dayLabel, $startTime–$endTime",
                        style = MaterialTheme.typography.headlineSmall.copy(
                            fontWeight = FontWeight.Bold
                        ),
                        color = LocalOnColours.current.onSurface
                    )
                    Spacer(modifier = Modifier.height(4.dp))
                    Text(
                        text = workplaceName,
                        style = MaterialTheme.typography.bodyMedium,
                        color = LocalOnColours.current.onSurfaceVariant
                    )
                }

                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(50))
                        .background(MaterialTheme.colorScheme.secondary) // Gold
                        .padding(horizontal = 16.dp, vertical = 6.dp)
                ) {
                    Text(
                        text = shift.requiredRole
                            ?: stringResource(Res.string.dashboard_staff_role),
                        color = MaterialTheme.colorScheme.onSecondary, // DeepNavy
                        style = MaterialTheme.typography.labelLarge.copy(fontWeight = FontWeight.Bold)
                    )
                }
            } else {
                Text(
                    text = stringResource(Res.string.dashboard_no_upcoming_shifts),
                    style = MaterialTheme.typography.headlineSmall,
                    color = LocalOnColours.current.onSurface
                )
            }
        }
    }
}

@Composable
fun TeamScheduleCard(
    activeEmployees: List<String>,
    upcomingEmployees: List<String>,
    completedEmployees: List<String>,
    upcomingDate: String?, // "YYYY-MM-DD"
    /** Opens the team; null when the person does not manage it (the card is not clickable then). */
    onViewDetails: (() -> Unit)?,
    modifier: Modifier = Modifier
) {
    val today = now().toLocalDateTime(TimeZone.currentSystemDefault()).date
    val tomorrow = today + DatePeriod(days = 1)
    
    val upcomingLabel = when (upcomingDate) {
        today.toString() -> stringResource(Res.string.dashboard_working_next)
        tomorrow.toString() -> "${stringResource(Res.string.dashboard_working_next)} (${stringResource(Res.string.dashboard_tomorrow)})"
        null -> stringResource(Res.string.dashboard_working_next)
        else -> "${stringResource(Res.string.dashboard_working_next)} ($upcomingDate)"
    }

    RegularCard(
        modifier = modifier.fillMaxWidth(),
        onClick = onViewDetails
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            // Title
            Text(
                text = stringResource(Res.string.dashboard_team_schedule),
                style = MaterialTheme.typography.titleMedium.copy(
                    fontWeight = FontWeight.Bold
                ),
                color = DeepNavy.copy(alpha = 0.7f)
            )
            Spacer(modifier = Modifier.height(16.dp))

            // Currently at Work
            ScheduleSection(
                title = stringResource(Res.string.dashboard_currently_at_work),
                employees = activeEmployees,
                indicatorColor = Color(0xFF2E7D32) // Green
            )

            Spacer(modifier = Modifier.height(16.dp))

            // Working Next
            ScheduleSection(
                title = upcomingLabel,
                employees = upcomingEmployees,
                indicatorColor = Color(0xFFFBC02D), // Yellow
                emptyText = stringResource(Res.string.dashboard_no_one_scheduled)
            )

            // Was Working Today (Only show if not empty)
            if (completedEmployees.isNotEmpty()) {
                Spacer(modifier = Modifier.height(16.dp))
                ScheduleSection(
                    title = stringResource(Res.string.dashboard_was_working_today),
                    employees = completedEmployees,
                    indicatorColor = Color(0xFF1976D2) // Blue
                )
            }
        }
    }
}

@Composable
private fun ScheduleSection(
    title: String,
    employees: List<String>,
    indicatorColor: Color,
    emptyText: String = stringResource(Res.string.dashboard_no_one_working)
) {
    Column {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(
                modifier = Modifier
                    .size(8.dp)
                    .clip(CircleShape)
                    .background(indicatorColor)
            )
            Spacer(modifier = Modifier.width(8.dp))
            Text(
                text = title,
                style = MaterialTheme.typography.bodyMedium.copy(
                    fontWeight = FontWeight.Bold
                ),
                color = DeepNavy
            )
        }
        Spacer(modifier = Modifier.height(8.dp))
        
        if (employees.isEmpty()) {
            Text(
                text = emptyText,
                style = MaterialTheme.typography.bodyMedium,
                color = DeepNavy.copy(alpha = 0.7f),
                modifier = Modifier.padding(start = 16.dp)
            )
        } else {
            employees.take(3).forEach { name ->
                Text(
                    text = com.evoapps.evoshift.ui.util.personName(name),
                    style = MaterialTheme.typography.bodyMedium,
                    color = DeepNavy,
                    modifier = Modifier.padding(start = 16.dp, bottom = 4.dp)
                )
            }
            if (employees.size > 3) {
                Text(
                    text = stringResource(Res.string.dashboard_more_employees, employees.size - 3),
                    style = MaterialTheme.typography.labelSmall,
                    modifier = Modifier.padding(start = 16.dp, top = 2.dp),
                    color = DeepNavy.copy(alpha = 0.7f)
                )
            }
        }
    }
}
/*
 * SH-247: the dashboards' cards while their data is on its way, each the size of the card it
 * stands for (see widgets/Skeleton.kt).
 */

/** [NextShiftCard]'s navy card with its label and the shapes of the day, the time and the role. */
@Composable
fun NextShiftPlaceholder(modifier: Modifier = Modifier) {
    com.evoapps.evoshift.widgets.SkeletonArea(modifier.fillMaxWidth()) {
        StandoutCard(modifier = Modifier.fillMaxWidth().height(180.dp).padding(vertical = 8.dp)) {
            Column(
                modifier = Modifier.padding(24.dp).fillMaxSize(),
                verticalArrangement = Arrangement.SpaceBetween
            ) {
                Text(
                    text = stringResource(Res.string.dashboard_next_shift),
                    style = MaterialTheme.typography.labelMedium.copy(letterSpacing = 1.sp, fontWeight = FontWeight.Bold),
                    color = LocalOnColours.current.accent
                )
                Column {
                    com.evoapps.evoshift.widgets.SkeletonText(style = MaterialTheme.typography.headlineSmall, widthFraction = 0.7f)
                    Spacer(modifier = Modifier.height(4.dp))
                    com.evoapps.evoshift.widgets.SkeletonText(widthFraction = 0.4f)
                }
                com.evoapps.evoshift.widgets.SkeletonBlock(Modifier.width(96.dp).height(30.dp), RoundedCornerShape(50))
            }
        }
    }
}

/** [TeamScheduleCard]: its title, then the "at work" and "working next" lines. */
@Composable
fun TeamSchedulePlaceholder(modifier: Modifier = Modifier) =
    com.evoapps.evoshift.widgets.SkeletonCard(modifier, lines = 4, title = stringResource(Res.string.dashboard_team_schedule))

/** "Today at work": its title and a few people's rows. */
@Composable
fun TodayAtWorkPlaceholder(modifier: Modifier = Modifier) {
    com.evoapps.evoshift.widgets.SkeletonArea(modifier.fillMaxWidth()) {
        RegularCard(Modifier.fillMaxWidth()) {
            Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                Text(
                    stringResource(Res.string.at_work_title),
                    style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                    color = DeepNavy.copy(alpha = 0.7f)
                )
                repeat(3) { com.evoapps.evoshift.widgets.SkeletonListRow(leadingSize = 32.dp) }
            }
        }
    }
}

/** A chart card: a title line and the plot ([plotHeight] and its legend). */
@Composable
fun ChartPlaceholder(modifier: Modifier = Modifier, plotHeight: androidx.compose.ui.unit.Dp = 160.dp) =
    com.evoapps.evoshift.widgets.SkeletonCard(modifier, lines = 0, blockHeight = plotHeight + 48.dp)

/**
 * SH-226 / SH-247: the dashboards' "Weather in the coming days" slot: the card when the workplace
 * has weather on; its placeholder while that is not known yet on a dashboard still loading;
 * nothing otherwise (weather off, or the setting read after the dashboard: then it unfolds).
 */
fun upcomingWeatherSlot(weather: com.evoapps.evoshift.viewmodels.UpcomingWeatherState, dashboardLoaded: Boolean): (@Composable () -> Unit)? =
    if (weather.on || (!weather.known && !dashboardLoaded)) {
        {
            com.evoapps.evoshift.widgets.LoadingSwap(
                known = weather.on,
                placeholder = {
                    com.evoapps.evoshift.widgets.SkeletonCard(
                        lines = 0,
                        blockHeight = 96.dp,
                        title = stringResource(Res.string.weather_strip_title)
                    )
                }
            ) {
                com.evoapps.evoshift.ui.util.WeatherForecastCard(weather.days, weather.today)
            }
        }
    } else null

/** A navy figure card (the labour %): its title line, the big figure and a line under it. */
@Composable
fun FigurePlaceholder(modifier: Modifier = Modifier) {
    com.evoapps.evoshift.widgets.SkeletonArea(modifier.fillMaxWidth()) {
        StandoutCard(modifier = Modifier.fillMaxWidth()) {
            Column(Modifier.padding(24.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                com.evoapps.evoshift.widgets.SkeletonText(style = MaterialTheme.typography.titleMedium, widthFraction = 0.5f)
                com.evoapps.evoshift.widgets.SkeletonBlock(Modifier.width(120.dp).height(48.dp))
                com.evoapps.evoshift.widgets.SkeletonText(style = MaterialTheme.typography.bodySmall, widthFraction = 0.7f)
            }
        }
    }
}
